/* istanbul ignore file */

import { Promise } from 'bluebird';
import { uniqueId } from 'underscore';
import { FILE_ERROR_TYPES, DEFAULT_MAX_SIZE } from './constants';

export function validateFiles({
  files,
  minFileSize = 0,
  allowedTypes = [],
  maxFileSize = DEFAULT_MAX_SIZE,
}) {
  return new Promise((resolve) => {
    const validationResult = {
      files: [],
      hasError: false,
    };
    for (let i = 0; i < files.length; i += 1) {
      const file = files[i];
      file.id = uniqueId('file-');
      const fileTypesToCheck = allowedTypes.map(type =>
        type.replace('.', '').toLowerCase());
      const fileType = file.type.includes('/')
        ? file.type.split('/')[1].toLowerCase()
        : file.type.toLowerCase();
      if (!fileTypesToCheck.includes(fileType)) {
        file.errorType = FILE_ERROR_TYPES.FILE_TYPE.type;
        file.message = FILE_ERROR_TYPES.FILE_TYPE.message;
        validationResult.files.push(file);
        validationResult.hasError = true;
      } else if (file.size > maxFileSize || file.size < minFileSize) {
        file.errorType = FILE_ERROR_TYPES.FILE_SIZE.type;
        file.message = FILE_ERROR_TYPES.FILE_SIZE.message;
        validationResult.files.push(file);
        validationResult.hasError = true;
      } else {
        file.message = _TL_('File is valid');
        validationResult.files.push(file);
      }
    }
    resolve(validationResult);
  });
}

function getLocalizedFileSizeTextsBySize(fileSize, i18n) {
  return [
    i18n.getString(_TL_('{{fileSize}} Bytes'), { fileSize }),
    i18n.getString(_TL_('{{fileSize}} KB'), { fileSize }),
    i18n.getString(_TL_('{{fileSize}} MB'), { fileSize }),
    i18n.getString(_TL_('{{fileSize}} GB'), { fileSize }),
    i18n.getString(_TL_('{{fileSize}} TB'), { fileSize }),
    i18n.getString(_TL_('{{fileSize}} PB'), { fileSize }),
    i18n.getString(_TL_('{{fileSize}} EB'), { fileSize }),
    i18n.getString(_TL_('{{fileSize}} ZB'), { fileSize }),
    i18n.getString(_TL_('{{fileSize}} YB'), { fileSize }),
  ];
}

export function formatFileSize(bytes, decimals = 2, i18n) {
  if (bytes === 0) return i18n.getString(_TL_('0 Bytes'));
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizeUnitIndex = Math.floor(Math.log(bytes) / Math.log(k));
  const formattedFileSize = i18n.formatDecimal(bytes / (k ** sizeUnitIndex), {
    maximumFractionDigits: dm,
    minimumFractionDigits: 0,
  });
  const localizedFileSizeTexts = getLocalizedFileSizeTextsBySize(
    formattedFileSize,
    i18n
  );
  return localizedFileSizeTexts[sizeUnitIndex];
}

const mockFileUploadAPI = () =>
  new Promise((resolve, reject) => {
    const shouldReject = Math.random() > 0.5;
    setTimeout(() => {
      if (shouldReject) {
        reject(new Error(_TL_('Upload Error. Failed to upload file')));
      } else {
        resolve();
      }
    }, 2000);
  });

export async function uploadFiles(filesToUpload) {
  const uploadFile = async (file) => {
    try {
      await mockFileUploadAPI(file);
      // eslint-disable-next-line no-param-reassign
      file.uploaded = true;
      // eslint-disable-next-line no-param-reassign
      file.message = _TL_('File uploaded successfully');
    } catch (error) {
      // eslint-disable-next-line no-param-reassign
      file.uploaded = false;
      // eslint-disable-next-line no-param-reassign
      file.errorType = FILE_ERROR_TYPES.UPLOAD_FAILED.type;
      // eslint-disable-next-line no-param-reassign
      file.message =
        error.message || FILE_ERROR_TYPES.UPLOAD_FAILED.message;
    }
    return file;
  };

  const updatedFiles = await Promise.all(filesToUpload.map(file => uploadFile(file)));
  return updatedFiles;
}
